import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import {
  getForms,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  copyForm,
} from '../api/formsAPI';
import { getFormType } from '../api/form-typeAPI';
import {
  createFormItem,
  updateFormItem,
  deleteFormItem,
} from '../api/form-itemsAPI';
import { getFormInputType } from '../api/form-input-typeAPI';
import { fetchData } from '../utils/apiHelpers';
import {
  getFormAssignments,
  createFormAssignment,
  updateFormAssignment,
} from '../api/form-assignmentsAPI';
import { getActions } from '../api/actionsAPI';
import { getEmailGroups } from '../api/email-groupAPI';
import { getFormActions, createFormAction, deleteFormAction } from '../api/form-actionsAPI';
import { checkFormExists } from '../api/work-order-formsAPI';
import FormsGeneralTab from './FormsGeneralTab';
import FormDetailsTab from './FormDetailsTab';
import FormAssignmentTab from './FormAssignmentTab';
import { renderPreviewInput as previewRenderer } from '../utils/renderPreviewInput';
import { mageIconOptions, getMageIconLabel } from '../constants/mageIconOptions';
import FormStoreModal from './FormStoreModal';

const FormsLayer = () => {
  const userProfile = useUserProfile();
  const [forms, setForms] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  const [newForm, setNewForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsModalTab, setDetailsModalTab] = useState('header');
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [formItems, setFormItems] = useState([]);
  const [formInputTypes, setFormInputTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [actionsList, setActionsList] = useState([]);
  const [formActionRows, setFormActionRows] = useState([]);
  const [emailGroups, setEmailGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [activeFormType, setActiveFormType] = useState('all');
  const [dragItemId, setDragItemId] = useState(null);
  const [copyingFormId, setCopyingFormId] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const navigate = useNavigate();
  const { id: formId } = useParams();
  const openForm = (id) => navigate(`/forms/${id}`);
  const [formAssignment, setFormAssignment] = useState({
    assetClassIds: [],
    assetGroupIds: [],
    departmentIds: [],
    teamIds: [],
    roleIds: [-1],
    allDepartments: 0,
    allTeams: 0,
    allRoles: 1,
    allClasses: 0,
    allGroups: 0,
    appActionButton: 0,
    appSortOrder: 1,
    appFormName: '',
    desktopIcon: '',
    appIcon: '',
  });
  const [formAssignmentId, setFormAssignmentId] = useState(null);
  const itemsContainerRef = useRef(null);
  const [toastMsg, setToastMsg] = useState('');

  const normalizeActionId = (value) =>
    value === null || value === undefined || value === ''
      ? ''
      : String(value);

  const normalizeFormTags = (tags) => {
    if (Array.isArray(tags)) return tags.filter((tag) => String(tag).trim());
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) {
          return parsed.filter((tag) => String(tag).trim());
        }
      } catch (err) {
        console.warn('Unable to parse form tags:', err);
      }
    }
    return [];
  };

  const actionValidationFields = useMemo(() => {
    const fields = [];
    formActionRows.forEach((_, idx) => {
      fields.push({ field: `trigger_${idx}`, label: 'Trigger', required: true });
      fields.push({ field: `actionId_${idx}`, label: 'Action', required: true });
    });
    return fields;
  }, [formActionRows]);

  const actionFormData = useMemo(() => {
    const data = {};
    formActionRows.forEach((row, idx) => {
      data[`trigger_${idx}`] = row.trigger;
      data[`actionId_${idx}`] = row.actionId;
    });
    return data;
  }, [formActionRows]);

  const selectedActionIds = useMemo(
    () =>
      formActionRows
        .map((row) => normalizeActionId(row.actionId))
        .filter((id) => id),
    [formActionRows]
  );

  const {
    errors: actionErrors,
    setErrors: setActionErrors,
    validate: validateActionRows,
  } = useValidator(actionValidationFields, actionFormData);

  useEffect(() => {
    if (toastMsg) {
      const timer = setTimeout(() => setToastMsg(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMsg]);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  const selectStyles = {
    control: (base) => ({
      ...base,
      height: '38px',
      borderRadius: '6px'
    })
  };

  const canCreateForms = userProfile?.privileges?.includes('forms:create');

  const getIconLabel = (val) => getMageIconLabel(val) || '';

  const handleOpenDetailsModal = (tab = 'header') => {
    setDetailsModalTab(tab);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsModalTab('header');
  };

  const fetchForms = async () => {
    try {
      const data = await getForms();
      setForms(data);
    } catch (err) {
      console.error('Error fetching forms:', err);
    }
  };

  const fetchFormTypes = () => {
    fetchData(getFormType, {}, setFormTypes);
  };

  const fetchFormInputTypes = () => {
    fetchData(getFormInputType, {}, setFormInputTypes);
  };

  const fetchActionsList = async () => {
    try {
      const data = await getActions();
      setActionsList(data);
    } catch (err) {
      console.error('Error fetching actions:', err);
    }
  };

  const createEmptyActionRow = () => ({
    trigger: '',
    actionId: '',
    actionData: null,
    emailGroupId: '',
    actionName: '',
  });

  const fetchFormActionsList = async () => {
    if (!editingId) return;
    try {
      const data = await getFormActions();
      const existing = data
        .filter((fa) => fa.formId === editingId)
        .map((fa) => ({
          id: fa.id,
          trigger: 'On Form Close',
          name: fa.actionName,
          actionId: fa.formActionId,
          actionData: fa.actionData,
          actionName: fa.action?.actionName || fa.actionName || '',
          emailGroupId: (() => {
            if (!fa.actionData) return '';
            if (typeof fa.actionData === 'object') {
              return fa.actionData.emailGroupId || '';
            }
            if (typeof fa.actionData === 'string') {
              try {
                const parsed = JSON.parse(fa.actionData);
                return parsed?.emailGroupId || '';
              } catch (err) {
                return '';
              }
            }
            return '';
          })(),
        }));
      setFormActionRows(existing.length > 0 ? existing : [createEmptyActionRow()]);
    } catch (err) {
      console.error('Error fetching form actions:', err);
    }
  };

  const fetchEmailGroups = async () => {
    try {
      const data = await getEmailGroups();
      setEmailGroups(data);
    } catch (err) {
      console.error('Error fetching email groups:', err);
    }
  };

  const handleOpenStoreModal = () => setShowStoreModal(true);
  const handleCloseStoreModal = () => setShowStoreModal(false);

  useEffect(() => {
    fetchForms();
    fetchFormTypes();
    fetchFormInputTypes();
  }, []);

  useEffect(() => {
    if (showDetailsModal && detailsModalTab === 'actions' && editingId) {
      fetchActionsList();
      fetchFormActionsList();
      fetchEmailGroups();
    }
  }, [showDetailsModal, detailsModalTab, editingId]);

  useEffect(() => {
    if (formId) {
      handleEdit(Number(formId));
    }
  }, [formId]);

  useEffect(() => {
    setFormAssignment(prev => {
      if (prev.allDepartments && prev.departmentIds[0] !== -1) {
        return { ...prev, departmentIds: [-1] };
      }
      if (!prev.allDepartments && prev.departmentIds.length === 1 && prev.departmentIds[0] === -1) {
        return { ...prev, departmentIds: [] };
      }
      return prev;
    });
  }, [formAssignment.allDepartments]);

  useEffect(() => {
    setFormAssignment(prev => {
      if (prev.allTeams && prev.teamIds[0] !== -1) {
        return { ...prev, teamIds: [-1] };
      }
      if (!prev.allTeams && prev.teamIds.length === 1 && prev.teamIds[0] === -1) {
        return { ...prev, teamIds: [] };
      }
      return prev;
    });
  }, [formAssignment.allTeams]);

  useEffect(() => {
    setFormAssignment(prev => {
      if (prev.allRoles && prev.roleIds[0] !== -1) {
        return { ...prev, roleIds: [-1] };
      }
      if (!prev.allRoles && prev.roleIds.length === 1 && prev.roleIds[0] === -1) {
        return { ...prev, roleIds: [] };
      }
      return prev;
    });
  }, [formAssignment.allRoles]);

  useEffect(() => {
    setFormAssignment(prev => {
      if (prev.allClasses && prev.assetClassIds[0] !== -1) {
        return { ...prev, assetClassIds: [-1] };
      }
      if (!prev.allClasses && prev.assetClassIds.length === 1 && prev.assetClassIds[0] === -1) {
        return { ...prev, assetClassIds: [] };
      }
      return prev;
    });
  }, [formAssignment.allClasses]);

  useEffect(() => {
    setFormAssignment(prev => {
      if (prev.allGroups && prev.assetGroupIds[0] !== -1) {
        return { ...prev, assetGroupIds: [-1] };
      }
      if (!prev.allGroups && prev.assetGroupIds.length === 1 && prev.assetGroupIds[0] === -1) {
        return { ...prev, assetGroupIds: [] };
      }
      return prev;
    });
  }, [formAssignment.allGroups]);

  const slugToTitle = s =>
    s.toLowerCase().replace(/-/g, ' ').replace(/\b[a-z]/g, c => c.toUpperCase());

  const inputFields = useMemo(
    () => [
      { field: 'name', label: 'Form Name', type: 'text', size: 12, required: true },
      {
        field: 'formTypeId',
        label: 'Form Type',
        type: 'select',
        size: 12,
        options: formTypes.map((t) => ({ value: t.id, label: t.name })),
        required: true,
      },
      { field: 'description', label: 'Form Description', type: 'textarea', size: 12 },
      {
        field: 'formTags',
        label: 'Tags',
        type: 'tags',
        size: 12,
        placeholder: 'Type a tag and press Enter',
      },
      {
        field: 'image',
        label: 'Form Header Image',
        type: 'image',
        size: 12,
        directory: 'modelimages',
      },
    ],
    [formTypes]
  );

  const appButtonFields = useMemo(() => {
    const clearAppButtonErrors = () => {
      setErrors((prev) => {
        if (!prev.appSortOrder && !prev.appFormName && !prev.desktopIcon) {
          return prev;
        }
        const next = { ...prev };
        delete next.appSortOrder;
        delete next.appFormName;
        delete next.desktopIcon;
        return next;
      });
    };

    const fields = [
      {
        field: 'appActionButton',
        label: 'Do you want the form to show as an Action Button in the App?',
        type: 'switch',
        size: 12,
        onChange: (nextValue) => {
          const enabled = Boolean(nextValue);
          setFormAssignment((prev) => {
            const next = {
              ...prev,
              appActionButton: enabled ? 1 : 0,
            };

            if (enabled) {
              next.appSortOrder = prev.appSortOrder || 1;
            } else {
              next.appSortOrder = 1;
              next.appFormName = '';
              next.desktopIcon = '';
              next.appIcon = '';
              clearAppButtonErrors();
            }

            return next;
          });
        },
      },
    ];

    if (formAssignment?.appActionButton) {
      fields.push(
        {
          field: 'appSortOrder',
          label: 'App Sort Order',
          type: 'number',
          size: 4,
          step: 1,
          min: 0,
          inputMode: 'numeric',
          pattern: '\\d*',
          onChange: (e) => {
            const rawValue = e.target.value;
            const digitsOnly = rawValue.replace(/[^0-9]/g, '');
            const sanitized = digitsOnly === '' ? '' : String(parseInt(digitsOnly, 10));
            setFormAssignment((prev) => ({
              ...prev,
              appSortOrder: sanitized,
            }));
            setErrors((prev) => {
              if (!prev.appSortOrder) return prev;
              const next = { ...prev };
              delete next.appSortOrder;
              return next;
            });
          },
        },
        {
          field: 'appFormName',
          label: 'App Icon Name',
          type: 'text',
          size: 4,
          onChange: (e) => {
            const value = e.target.value;
            setFormAssignment((prev) => ({
              ...prev,
              appFormName: value,
            }));
            setErrors((prev) => {
              if (!prev.appFormName) return prev;
              const next = { ...prev };
              delete next.appFormName;
              return next;
            });
          },
        },
        {
          field: 'desktopIcon',
          label: 'App Icon',
          type: 'select',
          size: 4,
          required: true,
          options: mageIconOptions,
          formatOptionLabel: (opt) => (
            <div className="d-flex align-items-center gap-2">
              <Icon icon={opt.value} width={18} height={18} />
              <span>{slugToTitle(opt.label)}</span>
            </div>
          ),
          onChange: (opt) => {
            const iconValue = opt?.value ?? '';
            setFormAssignment((prev) => ({
              ...prev,
              desktopIcon: iconValue,
              appIcon: iconValue ? getIconLabel(iconValue) : '',
            }));
            setErrors((prev) => {
              if (!prev.desktopIcon) return prev;
              const next = { ...prev };
              delete next.desktopIcon;
              return next;
            });
          },
        }
      );
    }

    return fields;
  }, [formAssignment, mageIconOptions, slugToTitle, setErrors, getIconLabel, setFormAssignment]);

  const { renderField } = useFormBuilder(inputFields, newForm, setNewForm, errors);


  const usageFields = useMemo(() => [
    { field: 'departmentIds', label: 'Departments', type: 'multi-select', size: 6, api: 'hierarchy-structures', displayField: 'name', disabled: formAssignment.allDepartments },
    { field: 'allDepartments', label: 'All Departments', type: 'switch', size: 6 },
    { field: 'teamIds', label: 'Teams', type: 'multi-select', size: 6, api: 'teams', displayField: 'teamName', disabled: formAssignment.allTeams },
    { field: 'allTeams', label: 'All Teams', type: 'switch', size: 6 },
    { field: 'roleIds', label: 'Roles', type: 'multi-select', size: 6, api: 'roles', displayField: 'description', disabled: formAssignment.allRoles },
    { field: 'allRoles', label: 'All Roles', type: 'switch', size: 6 },
    { field: 'assetClassIds', label: 'Classes', type: 'multi-select', size: 6, api: 'class', displayField: 'className', disabled: formAssignment.allClasses },
    { field: 'allClasses', label: 'All Classes', type: 'switch', size: 6 },
    { field: 'assetGroupIds', label: 'Models / Groups', type: 'multi-select', size: 6, api: 'groups', displayField: 'groupName', disabled: formAssignment.allGroups },
    { field: 'allGroups', label: 'All Models / Groups', type: 'switch', size: 6 },
  ],
    [formAssignment]);

  const { renderField: renderUsageField } = useFormBuilder(
    usageFields,
    formAssignment,
    setFormAssignment,
    errors,
  );

  const handleEdit = async (id) => {
    try {
      const data = await getFormById(id);
      setNewForm({ ...data, formTags: normalizeFormTags(data.formTags) });
      setFormItems(
        (data.tblFormItems || []).sort((a, b) => a.sortOrder - b.sortOrder)
      );
      const assignments = await getFormAssignments();
      const assignment = assignments.find((a) => a.formId === id);
      if (assignment) {
        setFormAssignment({
          assetClassIds: assignment.assetClassIds || [],
          assetGroupIds: assignment.assetGroupIds || [],
          departmentIds: assignment.departmentIds || [],
          teamIds: assignment.teamIds || [],
          roleIds: assignment.roleIds || [],
          allDepartments: assignment.departmentIds && assignment.departmentIds[0] === -1 ? 1 : 0,
          allTeams: assignment.teamIds && assignment.teamIds[0] === -1 ? 1 : 0,
          allRoles: assignment.roleIds && assignment.roleIds[0] === -1 ? 1 : 0,
          allClasses: assignment.assetClassIds && assignment.assetClassIds[0] === -1 ? 1 : 0,
          allGroups: assignment.assetGroupIds && assignment.assetGroupIds[0] === -1 ? 1 : 0,
          appActionButton: assignment.appActionButton || 0,
          appSortOrder: assignment.appSortOrder || 1,
          appFormName: assignment.appFormName || '',
          desktopIcon: assignment.desktopIcon || '',
          appIcon:
            assignment.appIcon || getIconLabel(assignment.desktopIcon) || '',
        });
        setFormAssignmentId(assignment.id);
      } else {
        setFormAssignment({
          assetClassIds: [],
          assetGroupIds: [],
          departmentIds: [],
          teamIds: [],
          roleIds: [-1],
          allDepartments: 0,
          allTeams: 0,
          allRoles: 1,
          allClasses: 0,
          allGroups: 0,
          appActionButton: 0,
          appSortOrder: 1,
          appFormName: '',
          desktopIcon: '',
          appIcon: '',
        });
        setFormAssignmentId(null);
      }
      setEditingId(id);
    } catch (err) {
      console.error('Error loading form:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { exists } = await checkFormExists(id);
      if (exists) {
        setToastMsg('Cannot delete form, it is being used');
        setShowDiscardModal(false);
        return;
      }
      await deleteForm(id);
      await fetchForms();
      navigate('/forms');
    } catch (err) {
      console.error('Error deleting form:', err);
    } finally {
      setShowDiscardModal(false);
    }
  };

  const handleCopyForm = async (id) => {
    if (!id) return;
    try {
      setCopyingFormId(id);
      const duplicated = await copyForm(id);
      await fetchForms();
      if (duplicated?.id) {
        navigate(`/forms/${duplicated.id}`);
      }
    } catch (err) {
      console.error('Error copying form:', err);
      setToastMsg('Unable to copy form. Please try again.');
    } finally {
      setCopyingFormId(null);
    }
  };

  const createDefaultFormStructure = async (formId) => {
    const defaultItems = [
      { heading: 1, description: 'New Heading' },
      { heading: 0, description: 'New Item' },
    ];

    try {
      const createdItems = [];
      for (let idx = 0; idx < defaultItems.length; idx += 1) {
        const { heading, description } = defaultItems[idx];
        const payload = {
          formId,
          description,
          width: 12,
          images: [],
          heading,
          instructions: '',
          formInputTypeId: 1,
          required: 0,
          sortOrder: idx + 1,
          createdBy: userProfile.id,
        };
        const created = await createFormItem(payload);
        createdItems.push(created);
      }
      setFormItems(createdItems.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      console.error('Error creating default form items:', err);
    }
  };

  const handleSaveDetails = async () => {
    const formErrors = validateForm(inputFields, newForm);
    const assignmentErrors = {};

    if (formAssignment.appActionButton) {
      if (
        !formAssignment.appSortOrder ||
        Number(formAssignment.appSortOrder) <= 0 ||
        !Number.isInteger(Number(formAssignment.appSortOrder))
      ) {
        assignmentErrors.appSortOrder = 'Sort order must be a positive whole number';
      }

      if (!formAssignment.appFormName || !String(formAssignment.appFormName).trim()) {
        assignmentErrors.appFormName = 'Form App Icon Name is required when the App button is enabled';
      }
    }

    const combinedErrors = { ...formErrors, ...assignmentErrors };
    setErrors(combinedErrors);
    if (Object.keys(combinedErrors).length > 0) return;

    try {
      const payload = {};
      for (const key in newForm) {
        if (key === 'formTags') {
          payload.formTags = Array.isArray(newForm.formTags)
            ? newForm.formTags
            : null;
          continue;
        }
        if (newForm[key] !== null && typeof newForm[key] !== 'object') {
          payload[key] = newForm[key];
        }
      }

      let targetFormId = editingId;
      let created = null;

      if (editingId) {
        await updateForm(editingId, payload);
      } else {
        payload.createdBy = userProfile.id;
        created = await createForm(payload);
        if (created && created.id) {
          targetFormId = created.id;
          setEditingId(created.id);
          setNewForm({ ...created, formTags: normalizeFormTags(created.formTags) });
          setFormItems([]);
          await createDefaultFormStructure(created.id);
        }
      }

      if (!targetFormId) {
        await fetchForms();
        handleCloseDetailsModal();
        return;
      }

      const convertIds = (ids, allFlag) =>
        allFlag ? [-1] : ids.length ? ids : null;

      const assignPayload = {
        formId: targetFormId,
        assetClassIds: convertIds(formAssignment.assetClassIds, formAssignment.allClasses),
        assetGroupIds: convertIds(formAssignment.assetGroupIds, formAssignment.allGroups),
        departmentIds: convertIds(formAssignment.departmentIds, formAssignment.allDepartments),
        teamIds: convertIds(formAssignment.teamIds, formAssignment.allTeams),
        roleIds: convertIds(formAssignment.roleIds, formAssignment.allRoles),
        appActionButton: formAssignment.appActionButton,
        appSortOrder: formAssignment.appActionButton ? formAssignment.appSortOrder : 1,
        appFormName: formAssignment.appActionButton ? formAssignment.appFormName : '',
        desktopIcon: formAssignment.appActionButton ? formAssignment.desktopIcon : '',
        appIcon: formAssignment.appActionButton
          ? getIconLabel(formAssignment.desktopIcon)
          : '',
      };

      if (formAssignmentId) {
        await updateFormAssignment(formAssignmentId, assignPayload);
      } else {
        const newAssign = await createFormAssignment(assignPayload);
        setFormAssignmentId(newAssign.id);
      }

      await fetchForms();
      handleCloseDetailsModal();

      if (created?.id) {
        navigate(`/forms/${created.id}`);
      }
    } catch (err) {
      console.error('Error saving form:', err);
    }
  };

  const handleItemChange = (id, field, value) => {
    setFormItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, [field]: value } : it))
    );
  };

  const saveItemField = async (id, changes) => {
    try {
      await updateFormItem(id, {
        ...changes,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile.id,
      });
    } catch (err) {
      console.error('Error updating form item:', err);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await deleteFormItem(id);
      setFormItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      console.error('Error deleting form item:', err);
    }
  };

  const handleDragStartItem = (id) => {
    setDragItemId(id);
  };

  const handleDropItem = (id) => {
    if (dragItemId === null || dragItemId === id) return;
    const newOrder = [...formItems];
    const fromIndex = newOrder.findIndex((it) => it.id === dragItemId);
    const toIndex = newOrder.findIndex((it) => it.id === id);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    const updated = newOrder.map((it, idx) => ({ ...it, sortOrder: idx + 1 }));
    setFormItems(updated);
    updated.forEach((it) => {
      if (it.sortOrder !== formItems.find((f) => f.id === it.id)?.sortOrder) {
        saveItemField(it.id, { sortOrder: it.sortOrder });
      }
    });
    setDragItemId(null);
  };

  const addItem = async (headingVal) => {
    if (!editingId) return;
    try {
      let newDescription = 'New Item';
      const sort =
        formItems.reduce(
          (max, it) => (it.sortOrder > max ? it.sortOrder : max),
          0
        ) + 1;
      if (headingVal === 1) {
        newDescription = 'New Heading';
      }
      const payload = {
        formId: editingId,
        description: newDescription,
        width: 12,
        images: [],
        heading: headingVal,
        instructions: '',
        formInputTypeId: 1,
        required: 0,
        sortOrder: sort,
        createdBy: userProfile.id,
      };
      const created = await createFormItem(payload);
      setFormItems((prev) =>
        [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder)
      );
      setTimeout(() => {
        if (itemsContainerRef.current) {
          itemsContainerRef.current.scrollTop =
            itemsContainerRef.current.scrollHeight;
        }
      }, 0);
    } catch (err) {
      console.error('Error creating form item:', err);
    }
  };

  const handleAddActionRow = () => {
    setFormActionRows((prev) => [
      ...prev,
      createEmptyActionRow(),
    ]);
  };

  const clearActionError = (key) => {
    setActionErrors((prevErrors) => {
      if (!prevErrors[key]) return prevErrors;
      const { [key]: _removed, ...rest } = prevErrors;
      return rest;
    });
  };

  const handleActionRowChange = (idx, field, value) => {
    setFormActionRows((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const updatedRow = { ...row, [field]: value };
        if (field === 'trigger') {
          clearActionError(`trigger_${idx}`);
        }
        return updatedRow;
      })
    );
  };

  const requiresEmailGroup = (row) => row.actionName === 'emailform';
  const buildEmailGroupActionData = (value) =>
    value ? `{"emailGroupId" : "${value}"}` : null;

  const handleActionSelectChange = (idx, value) => {
    const numericValue = value ? Number(value) : '';
    const selected = actionsList.find((a) => a.id === Number(value));
    const isEmailForm = selected?.actionName === 'emailform';

    setFormActionRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
            ...row,
            actionId: numericValue,
            actionData: selected?.data ?? null,
            actionName: selected?.actionName || '',
            emailGroupId: isEmailForm ? row.emailGroupId || '' : '',
          }
          : row
      )
    );

    clearActionError(`actionId_${idx}`);
    if (!isEmailForm) {
      clearActionError(`emailGroupId_${idx}`);
    }
  };

  const handleEmailGroupChange = (idx, value) => {
    setFormActionRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
            ...row,
            emailGroupId: value,
            actionData: buildEmailGroupActionData(value),
          }
          : row
      )
    );

    clearActionError(`emailGroupId_${idx}`);
  };

  const handleDeleteActionRow = (idx) => {
    setFormActionRows((prev) => prev.filter((_, i) => i !== idx));
    setActionErrors({});
  };

  const handleSaveActions = async () => {
    const isValid = validateActionRows();
    const emailErrors = {};

    formActionRows.forEach((row, idx) => {
      if (requiresEmailGroup(row) && !row.emailGroupId) {
        emailErrors[`emailGroupId_${idx}`] = 'Select an email distribution list';
      }
    });

    setActionErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      formActionRows.forEach((_, idx) => {
        if (!emailErrors[`emailGroupId_${idx}`] && nextErrors[`emailGroupId_${idx}`]) {
          delete nextErrors[`emailGroupId_${idx}`];
        }
      });
      return { ...nextErrors, ...emailErrors };
    });

    if (!isValid || Object.keys(emailErrors).length > 0) {
      return;
    }
    try {
      // Remove existing form actions before saving the current list
      const existing = await getFormActions();
      const toDelete = existing.filter((fa) => fa.formId === editingId);
      for (const fa of toDelete) {
        await deleteFormAction(fa.id);
      }

      // Save the actions from the SetUp Action form
      for (const row of formActionRows) {
        await createFormAction({
          formId: editingId,
          formActionId: row.actionId,
          actionData:
            requiresEmailGroup(row)
              ? buildEmailGroupActionData(row.emailGroupId)
              : row.actionData ?? null,
          createdBy: userProfile.id || null,
        });
      }
    } catch (err) {
      console.error('Error saving form actions:', err);
    }
  };

  const handleImagesUpload = async (id, files) => {
    const uploaded = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/upload/modelimages`,
          {
            method: 'POST',
            body: fd,
            credentials: 'include',
          }
        );
        const data = await res.json();
        uploaded.push(data.key);
      } catch (err) {
        console.error('Image upload failed:', err);
      }
    }
    const current = formItems.find((it) => it.id === id)?.images || [];
    const imgs = [
      ...(Array.isArray(current) ? current : []),
      ...(Array.isArray(uploaded) ? uploaded : [])
    ];
    handleItemChange(id, 'images', imgs);
    await saveItemField(id, { images: imgs });
  };

  const handleRemoveImage = async (id, idx) => {
    const current = formItems.find((it) => it.id === id)?.images || [];
    const imgs = current.filter((_, i) => i !== idx);
    handleItemChange(id, 'images', imgs);
    await saveItemField(id, { images: imgs });
  };

  const getInputType = (id) => {
    return (
      formInputTypes.find((t) => t.id === id)?.inputType ||
      formInputTypes.find((t) => t.id === id)?.name ||
      ''
    ).toLowerCase();
  };

  const renderPreviewInput = (item) => previewRenderer(item, getInputType);

  const getFormTypeId = (form) => {
    if (form.formTypeId !== undefined && form.formTypeId !== null) {
      return form.formTypeId;
    }
    if (form.formType?.id !== undefined && form.formType?.id !== null) {
      return form.formType.id;
    }
    return null;
  };

  const filteredForms = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return forms
      .filter((form) => {
        const nameMatch = form.name?.toLowerCase().includes(term);
        const descMatch = form.description?.toLowerCase().includes(term);
        const typeMatch = form.formType?.name?.toLowerCase().includes(term);
        const tagsMatch = normalizeFormTags(form.formTags).some((tag) =>
          String(tag).toLowerCase().includes(term)
        );
        return nameMatch || descMatch || typeMatch || tagsMatch;
      })
      .filter((form) => {
        if (activeFormType === 'all') return true;
        if (activeFormType === 'uncategorised') {
          return getFormTypeId(form) === null;
        }
        const formTypeId = getFormTypeId(form);
        if (formTypeId === null) return false;
        return Number(formTypeId) === Number(activeFormType);
      });
  }, [forms, searchTerm, activeFormType]);

  const availableFormTypes = useMemo(() => {
    const sorted = formTypes
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    const hasUncategorised = forms.some((form) => getFormTypeId(form) === null);
    return hasUncategorised
      ? [...sorted, { id: 'uncategorised', name: 'Uncategorised' }]
      : sorted;
  }, [formTypes, forms]);

  useEffect(() => {
    if (activeFormType === 'all') return;
    if (activeFormType === 'uncategorised') {
      if (!forms.some((form) => getFormTypeId(form) === null)) {
        setActiveFormType('all');
      }
      return;
    }
    const exists = formTypes.some((type) => Number(type.id) === Number(activeFormType));
    if (!exists) {
      setActiveFormType('all');
    }
  }, [activeFormType, formTypes, forms]);

  const getActiveTypeName = () => {
    if (activeFormType === 'uncategorised') {
      return 'Uncategorised';
    }
    const match = formTypes.find((type) => Number(type.id) === Number(activeFormType));
    return match?.name || 'Uncategorised';
  };

  const groupedForms = useMemo(() => {
    if (filteredForms.length === 0) {
      return [];
    }

    const sortFormsByName = (list) =>
      list
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    if (activeFormType !== 'all') {
      return [
        {
          typeName: getActiveTypeName(),
          forms: sortFormsByName(filteredForms),
        },
      ];
    }

    const groups = filteredForms.reduce((acc, form) => {
      const typeName = form.formType?.name || 'Uncategorised';
      if (!acc[typeName]) {
        acc[typeName] = [];
      }
      acc[typeName].push(form);
      return acc;
    }, {});

    return Object.entries(groups)
      .map(([typeName, list]) => ({
        typeName,
        forms: sortFormsByName(list),
      }))
      .sort((a, b) => a.typeName.localeCompare(b.typeName, undefined, { sensitivity: 'base' }));
  }, [filteredForms, activeFormType]);
  const modals = (
    <>
      {showDetailsModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingId ? 'Form Details & Assignment' : 'Create a New Form'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDetailsModal}
                />
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${detailsModalTab === 'header' ? 'active' : ''}`}
                      onClick={() => setDetailsModalTab('header')}
                    >
                      Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${detailsModalTab === 'assignment' ? 'active' : ''}`}
                      onClick={() => setDetailsModalTab('assignment')}
                    >
                      Assignment
                    </button>
                  </li>
                  {editingId && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${detailsModalTab === 'actions' ? 'active' : ''}`}
                        onClick={() => setDetailsModalTab('actions')}
                      >
                        Actions
                      </button>
                    </li>
                  )}
                </ul>
                <div className="tab-content pt-3">
                  <FormsGeneralTab
                    active={detailsModalTab === 'header'}
                    inputFields={inputFields}
                    renderField={renderField}
                    appButtonFields={appButtonFields}
                    renderAppButtonField={renderUsageField}
                  />
                  <FormAssignmentTab
                    active={detailsModalTab === 'assignment'}
                    newForm={newForm}
                    usageFields={usageFields}
                    renderUsageField={renderUsageField}
                  />

                  <div
                    className={`tab-pane fade ${detailsModalTab === 'actions' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    <div className="mb-12 d-flex justify-content-end">
                      <button
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                        onClick={handleAddActionRow}
                      >
                        <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Action
                      </button>
                    </div>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Trigger</th>
                          <th>Action</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formActionRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <select
                                className="form-select"
                                value={row.trigger}
                                onChange={(e) =>
                                  handleActionRowChange(idx, 'trigger', e.target.value)
                                }
                              >
                                <option value="">Select trigger</option>
                                <option value="On Form Close">On Form Close</option>
                              </select>
                              {actionErrors[`trigger_${idx}`] && (
                                <div className="text-danger small">
                                  {actionErrors[`trigger_${idx}`]}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <select
                                  className="form-select"
                                  value={row.actionId || ''}
                                  onChange={(e) => handleActionSelectChange(idx, e.target.value)}
                                >
                                  <option value="">Select action</option>
                                  {actionsList
                                    .filter((act) => {
                                      const optionId = normalizeActionId(act.id);
                                      const rowActionId = normalizeActionId(row.actionId);

                                      if (!optionId) return true;
                                      if (optionId === rowActionId) return true;

                                      return !selectedActionIds.includes(optionId);
                                    })
                                    .map((act) => (
                                      <option key={act.id} value={act.id}>
                                        {act.name}
                                      </option>
                                    ))}
                                </select>
                                {requiresEmailGroup(row) && (
                                  <select
                                    className={`form-select ${actionErrors[`emailGroupId_${idx}`] ? 'is-invalid' : ''}`}
                                    value={row.emailGroupId || ''}
                                    onChange={(e) => handleEmailGroupChange(idx, e.target.value)}
                                  >
                                    <option value="">Select distribution list</option>
                                    {emailGroups.map((group) => (
                                      <option key={group.id} value={group.id}>
                                        {group.groupName || group.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              {actionErrors[`actionId_${idx}`] && (
                                <div className="text-danger small">
                                  {actionErrors[`actionId_${idx}`]}
                                </div>
                              )}
                              {requiresEmailGroup(row) && actionErrors[`emailGroupId_${idx}`] && (
                                <div className="text-danger small">
                                  {actionErrors[`emailGroupId_${idx}`]}
                                </div>
                              )}
                            </td>
                            <td>
                              <button
                                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() => handleDeleteActionRow(idx)}
                              >
                                <Icon
                                  icon="ic:baseline-delete-outline"
                                  className="icon text-xl line-height-1"
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="d-flex justify-content-end mt-3">
                      <button className="btn btn-primary" onClick={handleSaveActions}>
                        Save Actions
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseDetailsModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSaveDetails}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDiscardModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Discard Form</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDiscardModal(false)}
                />
              </div>
              <div className="modal-body">
                Do you want to delete this form?
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDiscardModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(editingId)}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <FormStoreModal
        isOpen={showStoreModal}
        onClose={handleCloseStoreModal}
        onInstallSuccess={fetchForms}
        renderPreviewInput={renderPreviewInput}
      />
      {toastMsg && (
        <div
          className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
          style={{ zIndex: 2000 }}
        >
          <div className="toast show align-items-center text-bg-danger border-0">
            <div className="d-flex">
              <div className="toast-body">{toastMsg}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setToastMsg('')}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (formId) {
    return (
      <div className="card h-100 p-0 rounded-0">
        <div
          className="card-body pt-0"
          style={{ height: "calc(100vh - 77px)" }}
        >
          <FormDetailsTab
            active
            formItems={formItems}
            formInputTypes={formInputTypes}
            selectStyles={selectStyles}
            itemsContainerRef={itemsContainerRef}
            addItem={addItem}
            handleDragStartItem={handleDragStartItem}
            handleDropItem={handleDropItem}
            handleItemChange={handleItemChange}
            saveItemField={saveItemField}
            handleImagesUpload={handleImagesUpload}
            handleRemoveImage={handleRemoveImage}
            handleDeleteItem={handleDeleteItem}
            renderPreviewInput={renderPreviewInput}
            getInputType={getInputType}
            newForm={newForm}
            onEditHeader={() => handleOpenDetailsModal('header')}
            onDiscardForm={() => setShowDiscardModal(true)}
            editingId={editingId}
            onCopyForm={handleCopyForm}
            isCopying={copyingFormId === editingId}
            canCopyForm={canCreateForms}
          />
        </div>
        {modals}
      </div>
    );
  }

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center p-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {/*{renderFilterControls()}*/}
          </div>
          {canCreateForms && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary text-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                onClick={() => {
                  setNewForm({ formTags: [] });
                  setEditingId(null);
                  setFormItems([]);
                  setFormAssignment({
                    assetClassIds: [],
                    assetGroupIds: [],
                    departmentIds: [],
                    teamIds: [],
                    roleIds: [-1],
                    appActionButton: 0,
                    appSortOrder: 1,
                    appFormName: '',
                    desktopIcon: '',
                    appIcon: '',
                    allDepartments: 0,
                    allTeams: 0,
                    allRoles: 1,
                    allClasses: 0,
                    allGroups: 0,
                  });
                  setFormAssignmentId(null);
                  setErrors({});
                  handleOpenDetailsModal('header');
                }}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Form
              </button>
              <button
                className="btn btn-outline-primary text-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                onClick={handleOpenStoreModal}
              >
                <Icon icon="mdi:arrow-collapse-down" className="icon text-xl" /> Import from Store
              </button>
            </div>
          )}
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-12 col-lg-auto mb-3 mb-lg-0 filter-sidebar">
              <div
                className="nav flex-lg-column gap-2"
                role="tablist"
                aria-orientation="vertical"
              >
                <button
                  type="button"
                  className={`filter-sidebar__button ${activeFormType === 'all' ? 'is-active' : ''}`}
                  aria-pressed={activeFormType === 'all'}
                  onClick={() => setActiveFormType('all')}
                >
                  All
                </button>
                {availableFormTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={`filter-sidebar__button ${activeFormType === type.id ? 'is-active' : ''}`}
                    aria-pressed={activeFormType === type.id}
                    onClick={() => setActiveFormType(type.id)}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-9">
              {filteredForms.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center h-100 py-5 text-neutral-500">
                  No forms found.
                </div>
              ) : (
                <div className="row g-3">
                  {groupedForms.map(({ typeName, forms: typeForms }) => (
                    <React.Fragment key={typeName}>
                      {typeForms.map((form) => {
                        const imageUrl = form.image
                          ? `${process.env.REACT_APP_SPACES_BASE}/${form.image}`
                          : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                        const formTags = normalizeFormTags(form.formTags);
                        return (
                          <div key={form.id} className="col-lg-3 col-md-4">
                            <div className="card h-100 p-0 rounded-0 overflow-hidden">
                              <div className="card-body">
                                <Link
                                  to="#"
                                  className="w-100 max-h-194-px radius-8 overflow-hidden"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openForm(form.id);
                                  }}
                                >
                                  <img
                                    src={imageUrl}
                                    alt=""
                                    className="w-100 object-fit-cover"
                                    style={{ height: "180px" }}
                                  />
                                </Link>
                                <div className="mt-20">
                                  <div className="d-flex align-items-center gap-6 justify-content-between flex-wrap mb-16">
                                    <Link
                                      to="#"
                                      className="px-20 py-2 bg-neutral-100 rounded-pill bg-hover-neutral-300 text-sm text-neutral-600 fw-medium"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        openForm(form.id);
                                      }}
                                    >
                                      {form.formType?.name || 'Uncategorised'}
                                    </Link>
                                    <div className="d-flex align-items-center gap-8 text-neutral-500 fw-medium">
                                      <i className="ri-calendar-2-line" />
                                      {new Date(form.createdAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <h6 className="mb-16">
                                    <Link
                                      to="#"
                                      className="text-line-2 text-hover-primary-600 text-xl transition-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        openForm(form.id);
                                      }}
                                    >
                                      {form.name}
                                    </Link>
                                  </h6>
                                  <p className="text-line-3 text-neutral-500">
                                    {form.description}
                                  </p>
                                  {formTags.length > 0 && (
                                    <div className="mt-12 d-flex flex-wrap gap-2">
                                      {formTags.map((tag, idx) => (
                                        <span key={`${form.id}-tag-${idx}`} className="badge bg-secondary-focus text-secondary fw-normal rounded-pill">
                                          {tag.toLowerCase()}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {modals}
      </div>
    </>
  );
};

export default FormsLayer;
